import { KeyRound, LogOut, PanelLeft, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { GlobalSearch } from "@/components/layout/global-search";
import { TrustedDevicesDialog } from "@/components/settings/users/trusted-devices-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ThemeToggle } from "@/components/theme-toggle";
import { useAuth } from "@/lib/auth";

interface TopbarProps {
  onToggleSidebar: () => void;
}

function initialsFor(name: string, email: string): string {
  const base = (name || email || "?").trim();
  const parts = base.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0]!.charAt(0) + parts[1]!.charAt(0)).toUpperCase();
  }
  return base.slice(0, 2).toUpperCase();
}

export function Topbar({ onToggleSidebar }: TopbarProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [trustedDevicesOpen, setTrustedDevicesOpen] = useState(false);
  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-background px-4">
      <Button
        variant="ghost"
        size="icon"
        aria-label="Toggle sidebar"
        onClick={onToggleSidebar}
        className="text-muted-foreground hover:text-foreground"
      >
        <PanelLeft className="h-4 w-4" />
      </Button>

      <GlobalSearch />

      <div className="ml-auto flex items-center gap-1">
        <ThemeToggle />
        {user ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-9 gap-2 px-2 text-muted-foreground hover:text-foreground"
                aria-label="Account menu"
              >
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted font-mono text-[10px] uppercase text-foreground">
                  {initialsFor(user.name, user.email)}
                </span>
                <span className="hidden text-xs sm:inline">{user.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="space-y-0.5">
                <span className="block text-sm font-medium text-foreground">
                  {user.name}
                </span>
                <span className="block text-xs font-normal text-muted-foreground">
                  {user.email}
                </span>
                <span className="block font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
                  {user.role}
                </span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  setTrustedDevicesOpen(true);
                }}
              >
                <ShieldCheck className="h-4 w-4" />
                Trusted devices
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  // Reuse the public reset-password flow — the user enters
                  // their email there and gets the same one-hour link.
                  // Pre-fills the email so they don't have to retype it.
                  navigate("/password-reset/request", {
                    state: { email: user.email },
                  });
                }}
              >
                <KeyRound className="h-4 w-4" />
                Change password
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  void logout();
                }}
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>

      <TrustedDevicesDialog
        open={trustedDevicesOpen}
        onOpenChange={setTrustedDevicesOpen}
      />
    </header>
  );
}
